// const fmsGlobal = getFmsGlobal();

import {getTbNameByJobName, JobNames} from "../../../../../framework.core/JobNames";
import React, {MutableRefObject, useContext, useEffect, useMemo, useState} from "react";
import {useTypedFastStorage} from "../../../../../hooks/useFastStorage";
import TextBox from "../../../../../ui/TextBox";
import InlineInputControlRow from "../../../../../ui/layout/InlineInputControlRow";
import SecondaryButton from "../../../../../ui/SecondaryButton";
import {IGeneralWithList} from "../../../../../ifmsTypes";
import Fieldset from "../../../../common/Fieldset";
import RadioButton from "../../../../../ui/RadioButton";
import Select from "../../../../../ui/Select";
import LeftRightSplit from "../../../../../ui/LeftRightSplit";
import Date from "../../../../../ui/Date";
import LabeledControl from "../../../../../ui/LabeledControl";
import Number from "../../../../../ui/Number";
import BranchDropDown from "../../../../../uiControls/BranchDropDown";
import ApplicationContext from "../../../../../ApplicationContext";
import useSystemSettings from "../../../../../hooks/useSystemSettings";
import { dateTextToIso } from "../../../../../dateUtils";
import JobListViewContext from "../../../../common/JobListViewContext";
import { JournalLedgerListItem } from "../../../../../fms.data/models/JournalLedgerListItem";
import { SearchModelBase } from "../../../../../fms.data/models/SearchModelBase";
import { NavigationType, openNewPage } from "../../../../../util/navigationHelpers";
import { useHistory } from "react-router";
import useGlobalOkDialog from "../../../../../hooks/useGlobalOkDialog";
import { getInitialSearch } from "../../../../../filtering";
import { listViewPagePerBatch, listViewPageSize } from "../../../../common/JobListView";

interface JournalStorageKeys {
  modelStorage: string,
};

interface JournalViewParams {
  id: string | undefined,
  onPopupClose?: () => void,
  jobName: JobNames
};

interface JournalLoadedDetailsViewParams {
  journal: IGeneralWithList,
  journalRef: MutableRefObject<IGeneralWithList|null>,
  setJournal: (x: Partial<IGeneralWithList>) => void,
  onPopupClose?: () => void,
  jobName: JobNames,
  tbId: string | undefined,
};

function getJournalStorageKeys(jobName: JobNames): JournalStorageKeys {
  return {modelStorage: `search-journal-${jobName}`}
}

const newIGeneralWithList = {list: []};

function JournalImpl(params: JournalViewParams) {
  const {jobName, id} = params;
  const {modelStorage} = getJournalStorageKeys(jobName);

  const [inStorage, setInStorage, removeJournalFromStorage, journalRef] = useTypedFastStorage<IGeneralWithList>(modelStorage, newIGeneralWithList);

  useEffect(() => {
      initialize();
      return () => removeJournalFromStorage()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  async function loadExisting() {
    setInStorage({ searchBy: 'number', type: 0, branch: 'ALL', dateFrom: '', dateTo: '', seqFrom: '', seqTo: '' });
  }

  async function initialize() {
      await loadExisting();
  }

  return (
    <JournalInitializedImpl
      journal={inStorage!}
      journalRef={journalRef}
      setJournal={setInStorage}
      onPopupClose={params.onPopupClose}
      jobName={jobName}
      tbId={id}
    />
  );
}

function JournalInitializedImpl({
                                  journal,
                                  journalRef,
                                  setJournal,
                                  onPopupClose,
                                  jobName,
                                  tbId,
                                }: JournalLoadedDetailsViewParams) {
  const systemSettings = useSystemSettings();
  const appContext = useContext(ApplicationContext);
  const jobListContext = useContext(JobListViewContext);
  const history = useHistory();
  const [showGlobalOkDialog] = useGlobalOkDialog();
  
  function onChange(change: Partial<IGeneralWithList>) {
    setJournal(change);
  }

  async function onSearch() {
    const filterOrderObj:Array<any> = [];
    const search: any = { ...getInitialSearch(), pageSize: listViewPageSize, pagePerBatch: listViewPagePerBatch };

    switch (journal.searchBy) {
      case "number":
        const apiResult = await appContext.api.getSearchJournalQuery(journal.type, "", journal.number);
        if (!apiResult || !apiResult.success) {
          await showGlobalOkDialog(apiResult?.message ? <p>{apiResult.message}</p> : <p>Failure to read</p>);
        }
        
        search.general1 = 'mblId';
        search.generalText1 = apiResult.result?.item2.toString()??"0";
        filterOrderObj.push({ 
          columnId: 'mblId', slot: 'general1', type: 'general', label: 'Master Id', value: search.generalText1 });

        const tbName = getTbNameByJobName(apiResult.result?.item1)?.toUpperCase()||'';
        search.general2 = 'mtbName';
        search.generalText2 = tbName.length > 10 ? tbName.substring(0, 10): tbName;
        filterOrderObj.push({
          columnId: 'mtbName', slot: 'general2', type: 'general', label: 'Master Table Name', value: search.generalText2 });
        
        break;
      case "date":
          search.date = 'postDate';
          search.dateFrom = journal.dateFrom;
          search.dateTo = journal.dateTo;
          filterOrderObj.push({ 
            columnId: 'postDate', slot: 'date', type: 'date', label: 'Date', value: `${journal.dateFrom} ~ ${journal.dateTo}` });
        break;
      case "updateDate":
          search.date = 'updateDate';
          search.dateFrom = journal.dateFrom;
          search.dateTo = journal.dateTo;
          filterOrderObj.push({ 
            columnId: 'updateDate', slot: 'date', type: 'date', label: 'Update Date', value: `${journal.dateFrom} ~ ${journal.dateTo}` });
        break;
      case "dateSeq":
          search.date = 'postDate';
          search.dateFrom = journal.dateFrom;
          search.dateTo = journal.dateTo;
          filterOrderObj.push({ 
            columnId: 'postDate', slot: 'date', type: 'date', label: 'Date', value: `${journal.dateFrom} ~ ${journal.dateTo}` });

          search.number = 'seq';
          search.numberFrom = journal.seqFrom;
          search.numberTo = journal.seqTo;
          filterOrderObj.push({ 
            columnId: 'seq', slot: 'number', type: 'number', label: 'Seq', value: `${journal.seqFrom} ~ ${journal.seqTo}` });
        break;
    }

    // Branch를 선택한 경우 Filter 설정
    if (systemSettings.enableBranch && journal.branch !== "ALL") {
      if (["date", "updateDate", "dateSeq"].includes(journal.searchBy)) {
        search.general1 = 'branch';
        search.generalText1 = journal.branch;
        filterOrderObj.push({ 
          columnId: 'branch', slot: 'general1', type: 'general', label: 'BR', value: search.generalText1 });
      } else {
        search.general3 = 'branch';
        search.generalText3 = journal.branch;
        filterOrderObj.push({ 
          columnId: 'branch', slot: 'general3', type: 'general', label: 'BR', value: search.generalText3 });
      }
    }

    const searchModel = JournalLedgerListItem.calculateSearchParameters(search, JournalLedgerListItem.MapSearchFields);
    jobListContext.setFilterForSearchModel('/common/joblistview/acJournal/accounting', searchModel as SearchModelBase, filterOrderObj);
    openNewPage(history, `/common/joblistview/acJournal/accounting`, NavigationType.newPage);

    if (onPopupClose) onPopupClose();
  }

  const DateComponent = useMemo(() => {
    return (
      <InlineInputControlRow label='Date' mode='w80' alignRight>
        <div style={{width: 200}}>
          <LeftRightSplit
            leftSize='80px'
            rightSize='calc(100% - 80px)'
            left={
              <Date 
                //hideIcon 
                value={journal.dateFrom} 
                onChange={dateFrom => {
                  if (!journal.dateTo && dateFrom && dateTextToIso(dateFrom)) {
                    onChange({ dateTo: dateFrom})
                  }
                  onChange({dateFrom})
                }} 
              />
            }
            right={
              <LabeledControl
                alignRight
                labelSize='35px'
                label='~'
                control={
                  <Date 
                    // hideIcon
                    disabled={!journal.dateFrom} 
                    value={journal.dateTo} 
                    onChange={dateTo => onChange({dateTo})} />
                }
              />
            }
          />
        </div>
      </InlineInputControlRow>
    )
  }, [journal.dateFrom, journal.dateTo]);

  const enableSearch = useMemo(() => {
    let result = false;
    switch (journal.searchBy) {
      case "number":
        if (journal.number) result = true;
        break;
      case "date":
      case "updateDate":
        if (journal.dateFrom && journal.dateTo) result = true;
        break;
      case "dateSeq":
        if (journal.dateFrom && journal.dateTo && journal.seqFrom && journal.seqTo) result = true;
        break;
    }
    return result;
  }, [journal.searchBy, journal.number, journal.dateFrom, journal.dateTo, journal.seqFrom, journal.seqTo]);

  return (
    <div className='report-popup'>
      <Fieldset label='Search By'>
        <InlineInputControlRow label='' mode='w190'>
          <div><RadioButton group="sendTo" reverse label="Number (B/L #, Ref. #, Invoice #, etc.)"
                            checked={journal.searchBy === 'number'}
                            onChecked={() => onChange({searchBy: 'number'})}/></div>
          <div><RadioButton group="sendTo" reverse label="Journal Date"
                            checked={journal.searchBy === 'date'}
                            onChecked={() => onChange({searchBy: 'date'})}/></div>
          <div><RadioButton group="sendTo" reverse label="Journal Date + Seq #"
                            checked={journal.searchBy === 'dateSeq'}
                            onChecked={() => onChange({searchBy: 'dateSeq'})}/></div>
          <div><RadioButton group="sendTo" reverse label="Update Date"
                            checked={journal.searchBy === 'updateDate'}
                            onChecked={() => onChange({searchBy: 'updateDate'})}/></div>
        </InlineInputControlRow>
      </Fieldset>
      <div className='report-row mt-2'>
        <div style={{display:'flex'}}>
          {systemSettings.enableBranch ?
          <InlineInputControlRow label="Branch" mode='w100' alignRight>
            <div style={{width: 80}}>
              <BranchDropDown
                selectId='journal-branch'
                value={journal.branch}
                onChange={(branch) => onChange({ branch })}
                disabled={false}
                addAll
                viewOnly
              />
            </div>
          </InlineInputControlRow> : <div style={{marginLeft:'100px'}}></div>
          }
          {journal.searchBy === 'number' && (
            <div style={{display: 'flex'}}>
              <InlineInputControlRow label='Type' mode='w80' alignRight>
                <div style={{width: 200}}>
                  <Select value={journal.type} onChange={e => onChange({type: e.target.value})}
                    items={[
                      { value: 0,  label: 'Ocean Import M B/L #'}, 
                      { value: 1,  label: 'Ocean Import Ref. #'}, 
                      { value: 2,  label: 'Ocean Export M B/L #'}, 
                      { value: 3,  label: 'Ocean Export Ref. #'}, 
                      { value: 4,  label: 'Air Import MAWB #'}, 
                      { value: 5,  label: 'Air Import Ref. #'}, 
                      { value: 6,  label: 'Air Export MAWB #'}, 
                      { value: 7,  label: 'Air Export Ref. #'}, 
                      { value: 8,  label: 'Other Shipment Ref. #'}, 
                      { value: 9,  label: 'Invoice #'}, 
                      { value: 10, label: 'Bill #'}, 
                      { value: 11, label: 'Credit/Debit Note #'}, 
                      { value: 12, label: 'Receivable Check'}, 
                      { value: 13, label: 'Payable Check'}, 
                    ]} 
                  />
                </div>
              </InlineInputControlRow>
              <InlineInputControlRow label='Number' mode='w80' alignRight>
                <div style={{width: 100}}>
                  <TextBox disabled={journal.type < 0} value={journal.number} onChange={e => onChange({number: e.target.value})} />
                </div>
              </InlineInputControlRow>
            </div>
          )}
          {journal.searchBy === 'date' && (
            <div style={{display: 'flex'}}>
              {DateComponent}
            </div>
          )}
          {journal.searchBy === 'dateSeq' && (
            <div style={{display: 'flex'}}>
              {DateComponent}
              <InlineInputControlRow label='Seq. #' mode='small' alignRight>
                <div style={{width: 100}}>
                  <LeftRightSplit
                    leftSize='40px'
                    rightSize='calc(100% - 40px)'
                    left={
                      <Number 
                        min={0} 
                        max={999}
                        value={journal.seqFrom}
                        onChange={seqFrom => {
                          onChange({seqFrom});
                          if (!journal.seqTo && seqFrom) {
                            onChange({ seqTo: seqFrom})
                          }
                        }}
                      />
                    }
                    right={
                      <LabeledControl
                        labelSize='20px'
                        label='  ~'
                        control={
                          <Number 
                            min={0} 
                            max={999} 
                            disabled={!journal.seqFrom} 
                            value={journal.seqTo}
                            onChange={seqTo => onChange({seqTo})}
                          />
                        }
                      />
                    }
                  />
                </div>
              </InlineInputControlRow>
            </div>
          )}
          {journal.searchBy === 'updateDate' && (
            <div style={{display: 'flex'}}>
              {DateComponent}
            </div>
          )}
        </div>
      </div>
      <div className='clearfix'/>
      <div className='report-footer'>
        <SecondaryButton
          size="regular"
          disabled={!enableSearch}
          onClick={onSearch}>
          SEARCH
        </SecondaryButton>
        <SecondaryButton
          size="regular"
          onClick={onPopupClose}>
          CANCEL
        </SecondaryButton>
      </div>
    </div>
  );
}

const JournalDetails = ({
                          onPopupClose,
                          jobName,
                          selectedElementId
                        }: { onPopupClose?: () => void, jobName: JobNames, selectedElementId: number }) => {
  // if (!selectedElementId) return null;

  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
   */


  return (
    <JournalImpl id={selectedElementId.toString()} jobName={jobName} onPopupClose={onPopupClose} key={`journal-details`}/>
  );
};
export default JournalDetails;
